import {
    Box,
    Button,
    Center,
    Divider,
    Flex,
    LinkFilesIcon,
    ReloadIcon,
    Spinner,
    Stack,
    Text,
    useDisclosure,
} from '@adamdotai/design-system'
import { useQuery, useSubscription } from '@urql/next'
import type { LexicalEditor } from 'lexical'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { Fragment, type RefObject, Suspense, useMemo } from 'react'
import type { DeepPartial } from 'utility-types'
import { CollapsableSection } from '@/components/CollapsableSection/CollapsableSection'
import { useAddSummaryToEditor } from '@/components/Editor/utils/useAddSummaryToEditor'
import { SummaryFeedback } from '@/components/MeetingRoom/MeetingRoomSidepanel/AICapture/Components/Summary/SummaryFeedback'
import { ReplaceNotes } from '@/components/Modals/aiCapture/ReplaceNotes'
import { graphql } from '@/generated/gql'
import { LinkEntityType, type MeetingSummary } from '@/generated/gql/graphql'
import { SummaryItem } from './SummaryItem'

export const GET_MEETING_SUMMARY = graphql(`
    query GetMeetingSummary($input: ViewMeetingInput!) {
        meetingV1(input: $input){
            meeting{
                id
                summary {
                    id
                    hasSubmittedFeedback
                    recommendedContentItems{
                        id
                        contentType
                        recommendedTitle
                        hasBeenChanged
                        subItems {
                            id
                            contentType
                            recommendedTitle
                            hasBeenChanged
                        }
                    }
                }
            }
        }
    }
`)

const SUMMARY_CREATED = graphql(`
    subscription SummaryCreated($meetingId: UUID!) {
        summaryCreatedV1(meetingId: $meetingId) {
            summary {
                id
            }
        }
    }
`)

type SectionProps = {
    summary: DeepPartial<MeetingSummary>
    meetingId: string
    editorReference: RefObject<LexicalEditor>
}

const Overview = ({ summary, meetingId, editorReference }: SectionProps) => {
    const t = useTranslations()
    const [overviewNote] =
        summary?.recommendedContentItems.filter(
            (i) => i.contentType === LinkEntityType.Note,
        ) ?? []

    if (overviewNote == null) {
        return null
    }

    return (
        <CollapsableSection
            customTitle={
                <Box
                    borderRadius="sm"
                    px="2"
                    color="tertiary.purple.1700"
                    bg="tertiary.purple.300"
                >
                    {t('common.overview')}
                </Box>
            }
            childrenProps={{
                pt: 2,
            }}
        >
            <SummaryItem
                id={overviewNote.id}
                editorReference={editorReference}
                isEdited={overviewNote.hasBeenChanged}
                isSubItem={false}
                meetingId={meetingId}
                title={overviewNote.recommendedTitle}
                type={LinkEntityType.Note}
            />
        </CollapsableSection>
    )
}

const Agenda = ({ summary, editorReference, meetingId }: SectionProps) => {
    const t = useTranslations()
    const items =
        summary?.recommendedContentItems.filter(
            (i) => i.contentType === LinkEntityType.Agenda,
        ) ?? []

    if (items.length === 0) {
        return null
    }

    return (
        <CollapsableSection
            customTitle={
                <Box
                    borderRadius="sm"
                    px="2"
                    color="success.1500"
                    bg="success.100"
                >
                    {t('glossary.agenda')}
                </Box>
            }
            childrenProps={{
                pt: 2,
            }}
        >
            {items.map((i) => (
                <Fragment key={i.id}>
                    <SummaryItem
                        editorReference={editorReference}
                        id={i.id}
                        isEdited={i.hasBeenChanged}
                        isSubItem={false}
                        meetingId={meetingId}
                        title={i.recommendedTitle}
                        type={LinkEntityType.Agenda}
                    />
                    {i.subItems.map((si) => (
                        <SummaryItem
                            key={si.id}
                            editorReference={editorReference}
                            id={si.id}
                            isEdited={si.hasBeenChanged}
                            isSubItem
                            meetingId={meetingId}
                            title={si.recommendedTitle}
                            type={LinkEntityType.Agenda}
                        />
                    ))}
                </Fragment>
            ))}
        </CollapsableSection>
    )
}

const ActionItems = ({ summary, editorReference, meetingId }: SectionProps) => {
    const t = useTranslations()
    const items =
        summary?.recommendedContentItems.filter(
            (i) => i.contentType === LinkEntityType.Action,
        ) ?? []

    if (items.length === 0) {
        return null
    }

    return (
        <CollapsableSection
            customTitle={
                <Box
                    borderRadius="sm"
                    px="2"
                    color="warning.1700"
                    bg="warning.300"
                >
                    {t('glossary.action-items')}
                </Box>
            }
            childrenProps={{
                pt: 2,
            }}
        >
            {items.map((i) => (
                <Fragment key={i.id}>
                    <SummaryItem
                        editorReference={editorReference}
                        id={i.id}
                        isEdited={i.hasBeenChanged}
                        isSubItem={false}
                        meetingId={meetingId}
                        title={i.recommendedTitle}
                        type={LinkEntityType.Action}
                    />
                    {i.subItems.map((si) => (
                        <SummaryItem
                            key={si.id}
                            editorReference={editorReference}
                            id={si.id}
                            isEdited={si.hasBeenChanged}
                            isSubItem
                            meetingId={meetingId}
                            title={si.recommendedTitle}
                            type={LinkEntityType.Action}
                        />
                    ))}
                </Fragment>
            ))}
        </CollapsableSection>
    )
}

const Decisions = ({ summary, editorReference, meetingId }: SectionProps) => {
    const t = useTranslations()
    const items =
        summary?.recommendedContentItems.filter(
            (i) => i.contentType === LinkEntityType.Decision,
        ) ?? []

    if (items.length === 0) {
        return null
    }

    return (
        <CollapsableSection
            customTitle={
                <Box
                    borderRadius="sm"
                    px="2"
                    color="information.1700"
                    bg="information.100"
                >
                    {t('glossary.decisions')}
                </Box>
            }
            childrenProps={{
                pt: 2,
            }}
        >
            {items.map((i) => (
                <Fragment key={i.id}>
                    <SummaryItem
                        editorReference={editorReference}
                        id={i.id}
                        isEdited={i.hasBeenChanged}
                        isSubItem={false}
                        meetingId={meetingId}
                        title={i.recommendedTitle}
                        type={LinkEntityType.Decision}
                    />
                    {i.subItems.map((si) => (
                        <SummaryItem
                            key={si.id}
                            editorReference={editorReference}
                            id={si.id}
                            isEdited={si.hasBeenChanged}
                            isSubItem
                            meetingId={meetingId}
                            title={si.recommendedTitle}
                            type={LinkEntityType.Decision}
                        />
                    ))}
                </Fragment>
            ))}
        </CollapsableSection>
    )
}

type SummaryProps = {
    meetingId: string
    editorReference: RefObject<LexicalEditor>
    isStackedView: boolean
    canManageMeetingContent: boolean
}

const SummaryImpl = ({
    meetingId,
    editorReference,
    isStackedView,
    canManageMeetingContent,
}: SummaryProps) => {
    const t = useTranslations()
    const { addSummaryToEditor, replaceContentWithSummary } =
        useAddSummaryToEditor({
            editor: editorReference.current,
        })
    const replaceNotesDisclosure = useDisclosure()
    const [{ data }] = useQuery({
        query: GET_MEETING_SUMMARY,
        variables: {
            input: {
                fakeId: meetingId,
            },
        },
        pause: meetingId == null,
        context: useMemo(() => ({ suspense: true }), []),
    })

    useSubscription({
        query: SUMMARY_CREATED,
        variables: {
            meetingId,
        },
        pause: meetingId == null,
    })

    const summary = data?.meetingV1?.meeting?.summary

    const onAddAllToNotes = () => {
        addSummaryToEditor({
            summary,
        })
    }

    const onReplaceNotes = () => {
        replaceNotesDisclosure.onClose()
        replaceContentWithSummary({ summary })
    }

    if (!data?.meetingV1?.meeting?.summary) {
        return (
            <Center w="full" h="full">
                <Stack alignItems="center" gap="4" maxW="70">
                    <Image
                        src={'/images/transcriptEmptyState.svg'}
                        width={250}
                        height={68}
                        alt="No Summary"
                    />
                    <Text align="center" color="neutral.1400">
                        {t(
                            'meetingroom.meeting-transcript-generated-once-uploaded',
                        )}
                    </Text>
                </Stack>
            </Center>
        )
    }

    return (
        <Stack
            h="full"
            maxH="full"
            overflow="hidden"
            gap="4"
            pt={isStackedView ? 3 : 6}
            px={isStackedView ? 6 : 0}
        >
            <Flex border="default" borderRadius="sm" alignItems="center" p="1">
                <Button
                    variant="ghost"
                    size="sm"
                    flex="1"
                    startIcon={<LinkFilesIcon />}
                    isDisabled={!canManageMeetingContent}
                    sx={{
                        color: 'tertiary.purple.1300',
                    }}
                    // @ts-expect-error button component handles color scheme, but props are not setup correctly
                    colorScheme="tertiary.purple"
                    onClick={onAddAllToNotes}
                >
                    {t('meetingroom.add-all-to-notes')}
                </Button>
                <Divider orientation="vertical" h="6" />
                <Button
                    variant="ghost"
                    size="sm"
                    flex="1"
                    startIcon={<ReloadIcon />}
                    isDisabled={!canManageMeetingContent}
                    sx={{
                        color: 'tertiary.purple.1300',
                    }}
                    // @ts-expect-error button component handles color scheme, but props are not setup correctly
                    colorScheme="tertiary.purple"
                    onClick={replaceNotesDisclosure.onOpen}
                >
                    {t('meetingroom.replace-notes')}
                </Button>
            </Flex>
            <Stack gap="6" flex="1" overflowY="scroll" overflowX="hidden">
                <Overview
                    summary={summary}
                    meetingId={meetingId}
                    editorReference={editorReference}
                />
                <Agenda
                    summary={summary}
                    meetingId={meetingId}
                    editorReference={editorReference}
                />
                <ActionItems
                    summary={summary}
                    meetingId={meetingId}
                    editorReference={editorReference}
                />
                <Decisions
                    summary={summary}
                    meetingId={meetingId}
                    editorReference={editorReference}
                />
            </Stack>
            {replaceNotesDisclosure.isOpen && (
                <ReplaceNotes
                    isOpen
                    onClose={replaceNotesDisclosure.onClose}
                    onReplace={onReplaceNotes}
                />
            )}
            {summary != null && !summary.hasSubmittedFeedback && (
                <SummaryFeedback />
            )}
        </Stack>
    )
}

const Summary = (props: SummaryProps) => {
    return (
        <Suspense
            fallback={
                <Center w="full" h="full">
                    <Spinner
                        size="sm"
                        color="primary.main"
                        label="loading..."
                    />
                </Center>
            }
        >
            <SummaryImpl {...props} />
        </Suspense>
    )
}

export { Summary }
